import { Fragment, ReactNode, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "./Button";

export type ConfirmModalVariant = "confirm" | "danger";

export type ConfirmModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmModalVariant;
  icon?: ReactNode;
  children?: ReactNode;
  confirmDisabled?: boolean;
};

/**
 * Modal de confirmação padronizado (marca APCAP / danger).
 */
export default function ConfirmModal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  variant = "confirm",
  icon,
  children,
  confirmDisabled = false,
}: ConfirmModalProps) {
  const cancelButtonRef = useRef(null);

  const iconWrap =
    variant === "danger"
      ? "bg-red-100 text-red-600"
      : "bg-apcap-muted text-apcap";

  const defaultIcon =
    variant === "danger" ? (
      <AlertTriangle className="h-6 w-6" aria-hidden />
    ) : (
      <CheckCircle2 className="h-6 w-6" aria-hidden />
    );

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-950/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-h-[90vh] transform overflow-y-auto rounded-2xl border border-zinc-200/80 bg-white px-4 pb-5 pt-6 text-left shadow-dock transition-all dark:border-zinc-800 dark:bg-zinc-900 sm:my-8 sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div
                    className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${iconWrap}`}
                  >
                    {icon ?? defaultIcon}
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-base font-semibold leading-6 text-zinc-900 dark:text-white"
                    >
                      {title}
                    </Dialog.Title>
                    {description ? (
                      <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                        {description}
                      </p>
                    ) : null}
                  </div>
                </div>

                {children ? <div className="mt-5">{children}</div> : null}

                <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Button
                    ref={cancelButtonRef}
                    type="button"
                    variant="outline"
                    onClick={onClose}
                  >
                    {cancelLabel}
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    disabled={confirmDisabled}
                    className={
                      variant === "danger"
                        ? "bg-red-600 hover:bg-red-700 focus-visible:ring-red-500"
                        : undefined
                    }
                    onClick={onConfirm}
                  >
                    {confirmLabel}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
